import React, { useState, useEffect, useCallback } from "react";
import { Briefcase, Loader2, Trash2, Plus, Mail, Sunrise, Clock, Bell, RefreshCw, Check, TrendingUp, TrendingDown } from "lucide-react";
import { portfolioGet, portfolioDigest, portfolioBulk, portfolioDelete, portfolioSettings, portfolioRunNow } from "../lib/api";
import { toast } from "sonner";
import { FloorCard } from "./FloorCard";
import { FloorJudgeCard } from "./FloorJudgeCard";
import { ResultsCard } from "./ResultsCard";
import { GexCard } from "./GexCard";
import { StrategyLabCard } from "./StrategyLabCard";
import { ResearchCard } from "./ResearchCard";
import { FlowCard } from "./FlowCard";
import { BucketsCard } from "./BucketsCard";

const money = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const clr = (v) => ((v || 0) > 0 ? "text-emerald-400" : (v || 0) < 0 ? "text-rose-400" : "text-cyan-200/60");

const SLOTS = [
  { id: "morning", label: "Morning", time: "8:00 AM CT", icon: Sunrise },
  { id: "noon", label: "Noon", time: "12:00 PM CT", icon: Clock },
  { id: "close", label: "Close", time: "3:15 PM CT", icon: Bell },
];

export default function NexusPortfolio({ session }) {
  const userId = session.user_id;
  const [data, setData] = useState({ holdings: [], email: "", auto: true, resend_configured: false });
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailDraft, setEmailDraft] = useState("");
  const [sending, setSending] = useState("");
  const [account, setAccount] = useState("regular");
  const [paste, setPaste] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([portfolioGet(userId), portfolioDigest(userId)]);
      setData(p); setEmailDraft(p.email || ""); setDigest(d.empty ? null : d);
    } catch (e) {} finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const saveEmail = async () => {
    try { const r = await portfolioSettings(userId, { email: emailDraft }); setData((s) => ({ ...s, email: r.email })); toast.success("Recipient saved"); }
    catch (e) { toast.error("Couldn't save"); }
  };
  const toggleAuto = async () => {
    try { const r = await portfolioSettings(userId, { auto: !data.auto }); setData((s) => ({ ...s, auto: r.auto })); }
    catch (e) { toast.error("Couldn't update"); }
  };
  const sendNow = async (slot) => {
    setSending(slot);
    try { const r = await portfolioRunNow(userId, slot); toast.success(`${slot} update emailed to ${r.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
    finally { setSending(""); }
  };
  const del = async (id) => { try { await portfolioDelete(userId, id); load(); } catch (e) {} };

  const addHoldings = async () => {
    // parse lines like "AAPL 50 150" or "AAPL,50,150" (ticker shares cost)
    const holdings = paste.split("\n").map((ln) => ln.trim()).filter(Boolean).map((ln) => {
      const p = ln.split(/[\s,]+/);
      return { ticker: p[0], shares: parseFloat(p[1]) || 0, cost_basis: p[2] != null ? parseFloat(p[2]) : null };
    }).filter((h) => h.ticker);
    if (!holdings.length) { toast.error("Enter at least one line: TICKER SHARES COST"); return; }
    setAdding(true);
    try {
      await portfolioBulk(userId, { account, holdings, replace: false });
      toast.success(`Added ${holdings.length} to ${account === "ira" ? "IRA" : "Regular"}`);
      setPaste(""); load();
    } catch (e) { toast.error("Couldn't add"); }
    finally { setAdding(false); }
  };

  const byAccount = (acct) => data.holdings.filter((h) => h.account === acct);
  const digestAccount = (acct) => (digest?.accounts?.[acct] || []);

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="portfolio-panel">
      <div className="max-w-4xl mx-auto space-y-5">
        <ResultsCard />
        <GexCard />
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Briefcase className="w-6 h-6" /> Portfolio Monitor</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Nexus watches your holdings and emails you 3 updates every weekday — morning, noon, and close. Notify-only, never trades.</p>
          </div>
          <button onClick={load} data-testid="portfolio-refresh" className="text-cyan-200/60 hover:text-[#00F0FF] p-2"><RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} /></button>
        </div>

        <BucketsCard />
        <FloorCard />
        <FloorJudgeCard />
        <StrategyLabCard />
        <FlowCard />
        <ResearchCard />

        {/* Totals */}
        {digest && (
          <div className="grid grid-cols-3 gap-3" data-testid="portfolio-totals">
            <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4">
              <p className="text-cyan-200/50 text-xs">Total value</p>
              <p className="text-cyan-50 text-xl sm:text-2xl font-extrabold mt-1">{money(digest.total_mv)}</p>
            </div>
            <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4">
              <p className="text-cyan-200/50 text-xs">Today</p>
              <p className={`text-xl sm:text-2xl font-extrabold mt-1 ${clr(digest.total_day)}`}>{money(digest.total_day)}</p>
            </div>
            <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4">
              <p className="text-cyan-200/50 text-xs">Total P&L</p>
              <p className={`text-xl sm:text-2xl font-extrabold mt-1 ${clr(digest.total_pnl)}`}>{money(digest.total_pnl)}</p>
            </div>
          </div>
        )}

        {/* Email settings + send now */}
        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-300/60" />
            <span className="text-cyan-100 text-sm font-semibold">Daily email updates</span>
            <button onClick={toggleAuto} data-testid="portfolio-auto-toggle"
              className={`ml-auto relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${data.auto ? "bg-[#00F0FF]" : "bg-white/10"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-[#030712] transition-transform ${data.auto ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} type="email" placeholder="recipient email" data-testid="portfolio-email-input"
              className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm outline-none" />
            <button onClick={saveEmail} data-testid="portfolio-email-save" className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Save</button>
          </div>
          {!data.resend_configured && <p className="text-amber-300/70 text-xs">⚠ Nexus email (Resend) isn't connected — updates can't be emailed yet.</p>}
          <div className="grid grid-cols-3 gap-2">
            {SLOTS.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => sendNow(s.id)} disabled={sending === s.id} data-testid={`portfolio-send-${s.id}`}
                  className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3 hover:bg-[#00F0FF08] transition-colors text-left disabled:opacity-50">
                  <div className="flex items-center gap-1.5 text-cyan-100 text-sm font-bold">
                    {sending === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 text-[#00F0FF]" />} {s.label}
                  </div>
                  <p className="text-cyan-300/40 text-[11px] mt-1">{s.time} · tap to send now</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Add holdings */}
        <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-2" data-testid="portfolio-add">
          <div className="flex items-center gap-2">
            <span className="text-cyan-100 text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add holdings</span>
            <div className="ml-auto flex gap-1">
              {["regular", "ira"].map((a) => (
                <button key={a} onClick={() => setAccount(a)} data-testid={`portfolio-acct-${a}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-bold border ${account === a ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33]"}`}>{a === "ira" ? "IRA" : "Regular"}</button>
              ))}
            </div>
          </div>
          <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} data-testid="portfolio-paste"
            placeholder={"One per line:  TICKER  SHARES  COST\nAAPL 50 150\nNVDA 30 90"}
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none font-mono resize-none" />
          <button onClick={addHoldings} disabled={adding} data-testid="portfolio-add-btn"
            className="px-4 py-2 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to {account === "ira" ? "IRA" : "Regular"}
          </button>
        </div>

        {/* Holdings tables */}
        {["regular", "ira"].map((acct) => {
          const rows = byAccount(acct);
          if (!rows.length) return null;
          const dg = digestAccount(acct);
          const dmap = Object.fromEntries(dg.map((r) => [r.ticker, r]));
          return (
            <div key={acct} className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4" data-testid={`portfolio-table-${acct}`}>
              <h3 className="text-cyan-100 font-bold mb-2">{acct === "ira" ? "IRA" : "Regular"} account</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-cyan-300/50 text-xs text-right">
                      <th className="text-left py-1.5">Ticker</th><th>Shares</th><th>Price</th><th>Day</th><th>Value</th><th>P&L</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((h) => {
                      const d = dmap[h.ticker] || {};
                      return (
                        <tr key={h.id} className="border-t border-white/5 text-right">
                          <td className="text-left py-1.5 font-mono font-bold text-cyan-50">{h.ticker}</td>
                          <td className="text-cyan-100/80">{h.shares || "—"}</td>
                          <td className="text-cyan-100/80">{d.price ? `$${d.price}` : "—"}</td>
                          <td className={clr(d.day_pct)}>{d.day_pct != null ? `${d.day_pct > 0 ? "+" : ""}${d.day_pct}%` : "—"}</td>
                          <td className="text-cyan-100/80">{money(d.market_value)}</td>
                          <td className={clr(d.pnl)}>{d.pnl != null ? money(d.pnl) : "—"}</td>
                          <td><button onClick={() => del(h.id)} data-testid="portfolio-del" className="text-cyan-200/30 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {!loading && data.holdings.length === 0 && (
          <div className="text-center py-12 text-cyan-200/40" data-testid="portfolio-empty">
            <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No holdings yet. Add your Regular and IRA positions above, and Nexus will email you 3 updates a day.</p>
          </div>
        )}
      </div>
    </div>
  );
}
